import React, { useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker, message } from "antd";
import { createLeaveRequest, updateLeaveRequest } from "../../../../core/data/redux/attendance/leave/leaveAction";
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../core/data/redux/store';


interface LeaveCancellationFormProps {
    selectedRowData: any;
    count?: { RemainingDays: any };
    onChange?: (form: any) => void;
    onClose: any
}

const LeaveCancellationForm: React.FC<LeaveCancellationFormProps> = ({
    selectedRowData,
    count,
    onClose,
    onChange,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const [cancellationForm, setCancellationForm] = useState<{
        LeaveTypeId: string | number;
        LeaveId: string | number;
        LeaveTypeName: any;
        LeaveMode: string;
        Cancellation_FromDate: Dayjs | null;
        Cancellation_ToDate: Dayjs | null;
        Cancellation_Duration: any;
        RemainingDays: string | number;
        CancellationReason: string;
        Cancellation_status: any;
        EmployeeId: any;
        LeaveRequestStatus: any;
    }>({
        LeaveTypeId: "",
        LeaveId: "",
        LeaveTypeName: "",
        LeaveMode: "",
        Cancellation_FromDate: null,
        Cancellation_ToDate: null,
        Cancellation_Duration: "",
        RemainingDays: "",
        CancellationReason: "",
        Cancellation_status: "Pending",
        EmployeeId: "",
        LeaveRequestStatus: ""
    });

    useEffect(() => {
        if (!selectedRowData) return;

        const today = dayjs();

        let fromDate = selectedRowData.FromDate ? dayjs(selectedRowData.FromDate) : null;
        const toDate = selectedRowData.ToDate ? dayjs(selectedRowData.ToDate) : null;

        if (fromDate && toDate) {
            // If today is between or equal to the date range → cancellation starts from today
            if (
                today.isSame(fromDate, "day") ||
                today.isBetween(fromDate, toDate, "day", "[]") // [] makes it inclusive
            ) {
                fromDate = today;
            }
        }

        // Calculate inclusive duration
        let duration = 0;
        if (fromDate && toDate) {
            const numberOfDays = Math.ceil((toDate.endOf("day").valueOf() - fromDate.startOf("day").valueOf()) / (1000 * 60 * 60 * 24));
            duration = selectedRowData.LeaveMode === "Half Day"
                ? numberOfDays * 0.5
                : numberOfDays;
        }


        setCancellationForm({
            LeaveTypeId: selectedRowData.LeaveTypeId || "",
            LeaveTypeName: selectedRowData.LeaveType || "",
            LeaveId: selectedRowData.Id || "",
            LeaveMode: selectedRowData.LeaveMode || "",
            Cancellation_FromDate: fromDate,
            Cancellation_ToDate: toDate,
            Cancellation_Duration: duration,
            RemainingDays: count?.RemainingDays || "",
            CancellationReason: "",
            Cancellation_status: "Pending",
            EmployeeId: selectedRowData.EmployeeId,
            LeaveRequestStatus: selectedRowData.LeaveRequestStatus,
        });
    }, [selectedRowData, count]);

    const [isLoading, setIsLoading] = useState(false);
    const handleFormChange = (field: string, value: any) => {
        const updated = { ...cancellationForm, [field]: value };
        setCancellationForm(updated);
        if (onChange) onChange(updated);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (isLoading) return;

        setIsLoading(true);

        const formattedPayload = {
            LeaveTypeId: selectedRowData.LeaveTypeId || "",
            LeaveTypeName: selectedRowData.LeaveType || "",
            LeaveId: selectedRowData.Id || "",
            LeaveMode: selectedRowData.LeaveMode || "",
            RemainingDays: count?.RemainingDays || "",
            CancellationReason: cancellationForm.CancellationReason,
            Cancellation_status: "Pending",
            EmployeeId: selectedRowData.EmployeeId,
            LeaveRequestStatus: selectedRowData.LeaveRequestStatus,
            FromDate: cancellationForm.Cancellation_FromDate
                ? dayjs(cancellationForm.Cancellation_FromDate).format("YYYY-MM-DD")
                : null,
            ToDate: cancellationForm.Cancellation_ToDate
                ? dayjs(cancellationForm.Cancellation_ToDate).format("YYYY-MM-DD")
                : null,
            Duration: Number(cancellationForm.Cancellation_Duration),
        };
        
        try {
            const res: any = await dispatch(createLeaveRequest(formattedPayload));

            if (res.meta.requestStatus === "fulfilled") {
                message.success("Cancellation Request created");
                onClose();
            } else {
                message.warning(res.payload.message);
            }
        } catch (error) {
            console.error("Error submitting cancellation:", error);
        } finally {
            setIsLoading(false); // ✅ Button re-enabled here
        }
    };



    return (
        <form onSubmit={handleSubmit}>
            <div className="drawer-body pb-0">
                <div className="row">
                    {/* Leave Type */}
                    <div className="col-md-6">
                        <div className="mb-3">
                            <label className="form-label">
                                Leave Type <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={cancellationForm.LeaveTypeName}
                                disabled
                            />
                        </div>
                    </div>

                    {/* Leave Mode */}
                    <div className="col-md-6">
                        <div className="mb-3">
                            <label className="form-label">
                                Leave Mode <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={cancellationForm.LeaveMode}
                                disabled
                            />
                        </div>
                    </div>

                    {/* From Date */}
                    <div className="col-md-6">
                        <div className="mb-3">
                            <label className="form-label">
                                From <span className="text-danger">*</span>
                            </label>
                            <DatePicker
                                className="form-control datetimepicker"
                                format="DD-MM-YYYY"
                                placeholder="DD-MM-YYYY"
                                value={cancellationForm.Cancellation_FromDate}
                                disabled
                            />
                        </div>
                    </div>

                    {/* To Date */}
                    <div className="col-md-6">
                        <div className="mb-3">
                            <label className="form-label">
                                To <span className="text-danger">*</span>
                            </label>
                            <DatePicker
                                className="form-control datetimepicker"
                                format="DD-MM-YYYY"
                                placeholder="DD-MM-YYYY"
                                value={cancellationForm.Cancellation_ToDate}
                                disabled
                            />
                        </div>
                    </div>

                    {/* Duration */}
                    <div className="col-md-6">
                        <div className="mb-3">
                            <label className="form-label">
                                No of Days <span className="text-danger">*</span>
                            </label>
                            <input
                                type="number"
                                className="form-control"
                                value={cancellationForm.Cancellation_Duration}
                                disabled
                            />
                        </div>
                    </div>

                    {/* Remaining Days */}
                    <div className="col-md-6">
                        <div className="mb-3">
                            <label className="form-label">
                                Remaining Days <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={cancellationForm.RemainingDays}
                                disabled
                            />
                        </div>
                    </div>
                    <div className="col-md-12">
                        <div className="mb-3">
                            <label className="form-label">
                                Cancellation Reason <span className="text-danger">*</span>
                            </label>
                            <textarea
                                className="form-control"
                                rows={2}
                                value={cancellationForm.CancellationReason}
                                onChange={(e) =>
                                    handleFormChange("CancellationReason", e.target.value)
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Submit button */}
            <div className="drawer-footer d-flex justify-content-end">
                <button
                    type="submit"
                    className="btn btn-danger"
                    disabled={isLoading}
                >
                    {isLoading ? "Processing..." : "Cancel Leave"}
                </button>
            </div>

        </form>
    );
};

export default LeaveCancellationForm;
